import { syncSentryUser, clearSentryUser } from './sentry-user';
import { API_ENDPOINTS, MONAMI_HOST } from '../config/env';
import {
  getCurrentPerson,
  clearCurrentPerson,
  clearCurrentUser,
  fetchCurrentUser,
  setCurrentUser,
  setCurrentPerson,
  UserNotFoundError,
} from '@/db/services/user_service';
import { refreshPrograms, refreshSites } from '@/db/services/program_site_service';
import { hasItem, removeItem, setItem } from '@/db/services/application_session_service';
import {
  clearDevice,
  getDeviceMetadata,
  getDeviceToken,
  hasDeviceToken,
  setDevice,
  setDeviceMetadata,
  DeviceMetadata,
} from '@/db/services/device_service';
import { fetchWithTimeout, AuthExpiredError, NetworkError, TimeoutError } from './fetch-utils';
import { createLogger, LogLevel } from './logger';
import { trackEvent, trackEventWithPerson } from '@/db/services/analytics_service';
import db from '@/db/db';

const logger = createLogger('Auth', LogLevel.INFO);

/**
 * Authentication utilities for managing user sessions.
 *
 * SESSION-HANDLING REQUIREMENTS — OFFLINE-FIRST CONTRACT
 * ========================================================================
 *
 * Mon Ami GO is an offline-first PWA. Users can spend hours of clinical
 * time disconnected from the server, accumulating unsaved or unsynced
 * work in the encrypted local DB. The auth model has to respect that.
 *
 * Three pieces of local state to keep distinct:
 *
 *   - `current_user_id`  (applicationSession)    — identifies the active
 *                                                  user. Presence = "the
 *                                                  client believes there
 *                                                  is a session".
 *   - `current_person`   (applicationSession)    — the selected
 *                                                  organization/person
 *                                                  context. Drives the
 *                                                  org-scoped queries.
 *   - encrypted DB       (Dexie tables)          — clients, documents,
 *                                                  signatures, etc.
 *                                                  Patient data and
 *                                                  unsaved work live here.
 *
 * The server session cookie is a fourth piece of state, owned by the
 * browser and inaccessible to JS. We don't try to mirror it locally.
 *
 * Rules:
 *
 *  1. **Session expiry must not destroy local data.** When the server
 *     returns 401 or session validation fails, we may clear
 *     `current_user_id` and route the user to /login, but the encrypted
 *     DB and `current_person` should survive whenever possible so the
 *     user can re-authenticate as the same user and continue exactly
 *     where they left off — including any unsaved drafts.
 *
 *  2. **`logout()` is the only path that wipes the DB.** Explicit user
 *     action to sign out, or a server-confirmed unrecoverable state
 *     (user record deleted on the server — see
 *     `useSessionValidator`'s `UserNotFoundError` branch). Implicit
 *     paths (session timeout, 401 on a routine fetch, validator
 *     drift) must NOT call logout(). "Server-confirmed" means Mon
 *     Ami's own `record_not_found` body, never a bare 404 status.
 *
 *  3. **Clearing `current_user_id` does not require clearing
 *     `current_person`.** Some clear paths happen to clear both
 *     (validator's "different server user" branch deliberately wipes
 *     the person because the local data belongs to someone else). But
 *     code reading auth state must not assume the pair stays linked —
 *     e.g. AuthWrapper's person-cleared effect re-verifies both
 *     independently rather than inferring one from the other.
 *
 *  4. **Re-auth flow on session expiry preserves continuity.** Routing
 *     to /login after a 401 is the user-visible signal. When they
 *     re-authenticate, `setSessionEstablished()` writes a new
 *     `current_user_id` and validates against the existing
 *     `current_person` (if any) — keeping their place if the same
 *     user came back, routing through /select-organization if it's
 *     someone else.
 *
 *  5. **Locked ≠ logged out.** The lock screen closes the encrypted DB
 *     but leaves both `current_user_id` and `current_person` in
 *     applicationSession (which is itself encrypted at rest via the
 *     dexie-encrypted middleware on the `value` field). Unlocking
 *     re-opens the DB; it does not re-authenticate against the server.
 *
 * Current clear/wipe sites and what they touch (audit when adding new
 * ones to make sure they satisfy rule 1):
 *
 *   - `logout()` (this file)                      → DB delete + clear caches
 *   - `useSessionValidator` UserNotFound          → calls logout() (rule 2)
 *   - `useSessionValidator` AuthExpired           → clears user_id + person*
 *   - `useSessionValidator` server-user-changed   → clears user_id + person
 *   - `fetch-utils.ts` 401 handler                → clears user_id + person*
 *
 *   * Paths marked with * could be tightened to clear only user_id (rule 1)
 *     so the user keeps their person/org and lands back where they were
 *     after re-auth. AuthWrapper must not assume the pair clear together.
 *
 *  6. **Only 401 indicates session expiry.** 403 means "authenticated but
 *     lacks permission" — it's a different signal and must NOT clear
 *     identifiers, the device token, or `current_user`. This applies
 *     uniformly across the auth surfaces:
 *
 *       - `fetchWithAuth` 401 → AuthExpiredError + clear. 403 → return
 *         response, caller decides.
 *       - `fetchCurrentUser` 401 → AuthExpiredError + clear. 403 → throw
 *         generic Error (no clear).
 *       - `registerDeviceIfNeeded` 401 → AuthExpiredError. 403 falls into
 *         the silent `!response.ok` fallback (a 403 here is a permission
 *         signal, not a session-expiry signal, so the cookie path keeps
 *         working).
 *       - `refreshDeviceIfStale` 401 → silent (the next regular API call
 *         will hit the same 401 and run the standard cleanup once).
 *         403 falls into the noisy `!response.ok` branch; the existing
 *         bearer keeps working until its hard expiry.
 *       - `checkSessionValidity` returns `response.ok`, so any non-2xx
 *         (including 403) reports the session as invalid — conservative
 *         on the unlock-time UI path, and a 403 from `/current_user` is
 *         not a real-world case because `/current_user` skips the
 *         offline-access authorization filter.
 */

export const LOGIN_URL = `${MONAMI_HOST}/api/offline/login/new`;
export const CURRENT_USER_ID_KEY = 'current_user_id';

/**
 * Check if user is authenticated
 * With session-based auth, we check if the current user ID is stored
 * The session cookie itself is HttpOnly and not accessible to JavaScript
 */
export async function isAuthenticated(): Promise<boolean> {
  return await hasItem(CURRENT_USER_ID_KEY);
}

/**
 * Best-effort: ensure a device-auth token is stored locally. Called from the
 * auth callback (fresh sign-in) and from AuthWrapper's bootstrap (existing
 * install transitioning to bearer auth). Cookies (still set on the online
 * app when the user signed in) are the only thing that authenticates the
 * `POST /api/offline/devices` call itself; from that point on the bearer
 * token replaces the cookie for every other `/api/offline/*` call.
 *
 * Forward-compat: if the server doesn't yet know about /api/offline/devices
 * (404/501/405) we silently fall back to cookie auth without storing a
 * token — that branch will start working when the server-side PR ships. A
 * 401 propagates back to the caller as AuthExpiredError so the standard
 * session-expired handling kicks in (no DB wipe, redirect to /login).
 */
// In-flight de-dupe so AuthCallback's setSessionEstablished() and
// useSessionValidator's validate() — which can fire near-simultaneously on a
// cold mount — don't both POST /devices and produce two server-side rows.
// The server is idempotent on the cookie, but we don't want to discard a
// freshly-issued token from one call when the other completes second.
let registerInFlight: Promise<DeviceMetadata | null> | null = null;

/**
 * @returns the freshly-registered DeviceMetadata when this call performed a
 *   new POST /devices, or null on every other path (already registered,
 *   offline, server doesn't speak the endpoint yet, network blip). Callers
 *   that want to emit `app.device_registered` analytics should fire on the
 *   non-null branch only — and at a point in their own flow where a current
 *   person is available, since the analytics service drops events without
 *   one. trackEvent was previously fired inline here, which dropped on first
 *   sign-in because setCurrentPerson hadn't run yet.
 */
export async function registerDeviceIfNeeded(): Promise<DeviceMetadata | null> {
  if (registerInFlight) return registerInFlight;
  registerInFlight = (async (): Promise<DeviceMetadata | null> => {
    // hasDeviceToken now propagates DB errors instead of swallowing them.
    // A transient DatabaseClosedError (e.g. during a lock/unlock window)
    // would otherwise look like "no token stored" and trigger a duplicate
    // device registration — overwriting the still-valid stored bearer
    // with a new one. Defer this tick on any DB read failure; the
    // validator will re-run register on its next online/visibility tick.
    let alreadyRegistered: boolean;
    try {
      alreadyRegistered = await hasDeviceToken();
    } catch (err) {
      logger.warn('Device token check failed — deferring registration (DB likely transitioning)', err);
      return null;
    }
    if (alreadyRegistered) return null;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return null;

    let response: Response;
    try {
      response = await fetchWithTimeout(API_ENDPOINTS.devices, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      });
    } catch (error) {
      if (error instanceof NetworkError || error instanceof TimeoutError) {
        logger.info('Device registration deferred — network error', error);
        return null;
      }
      logger.warn('Device registration deferred — unexpected error', error);
      return null;
    }

    // Server returns 404/405/501 when it doesn't yet support device registration
    // (the GO client may ship before the server does). Treat any of these as
    // "stay on cookie auth for now" and silently fall back.
    if (response.status === 404 || response.status === 405 || response.status === 501) {
      logger.info('Device registration endpoint not available — falling back to cookie auth');
      return null;
    }

    // Only 401 means "cookie is dead, kick the user to /login." A 403 here
    // is more plausibly "you're authenticated but lack permission to
    // register a device" (e.g., the org revoked offline-app access while
    // the user was signed in). Treating it as expiry would burn
    // current_user_id even though the session is still valid; the user
    // gets routed back to the online app's normal "not permitted" UI on
    // their next click instead.
    if (response.status === 401) {
      throw new AuthExpiredError();
    }

    if (!response.ok) {
      logger.warn(`Device registration failed with ${response.status} — falling back to cookie auth`);
      return null;
    }

    let payload: { token?: string; device?: DeviceMetadata } | null = null;
    try {
      payload = await response.json() as { token?: string; device?: DeviceMetadata };
    } catch (parseError) {
      // A 2xx response with malformed JSON shouldn't escape into the caller —
      // the documented contract for this helper is "silently fall back to
      // cookie auth on any non-success path." A SyntaxError out of .json()
      // would otherwise propagate through .finally() to whichever effect
      // awaited registerDeviceIfNeeded().
      logger.warn('Device registration response was not valid JSON — falling back to cookie auth', parseError);
      return null;
    }
    if (!payload?.token || !payload?.device?.id) {
      logger.warn('Device registration response missing token or device metadata — falling back to cookie auth');
      return null;
    }

    await setDevice(payload.token, payload.device);
    logger.info('Device registered', { id: payload.device.id, expires_at: payload.device.expires_at });

    return payload.device;
  })().finally(() => {
    registerInFlight = null;
  });
  return registerInFlight;
}

/**
 * Per-device-id dedupe for `app.device_registered`. `registerInFlight`
 * collapses the HTTP POST so only one device row gets created server-side,
 * but it returns the SAME resolved DeviceMetadata to every concurrent
 * awaiter. When `setSessionEstablished` and `useSessionValidator` race —
 * e.g. a stale validator mount that's still mid-validate when the auth
 * callback completes — both call sites would otherwise emit the same row.
 *
 * Keying by device.id (rather than a plain boolean) lets a subsequent
 * registration on the same page lifetime — softSignOut clears the bearer
 * → registerDeviceIfNeeded re-registers with a new server-side row →
 * different id — still emit, since that's a genuinely new device.
 */
let lastEmittedDeviceId: number | null = null;

/**
 * Helper for callers that want to emit `app.device_registered` analytics
 * after a successful device registration. Pass the DeviceMetadata returned
 * by `registerDeviceIfNeeded` plus a personOverride so the event survives
 * trackEvent's null-person short-circuit during fresh sign-in (where
 * setCurrentPerson hasn't run yet). Idempotent per device id — see
 * `lastEmittedDeviceId` for the race this guards.
 */
export function trackDeviceRegisteredEvent(
  device: DeviceMetadata,
  personOverride?: { personId: number; organizationId: number },
): void {
  if (lastEmittedDeviceId === device.id) return;
  lastEmittedDeviceId = device.id;
  const data = { device_id: device.id, expires_at: device.expires_at };
  // Pick the auth-aware variant when the caller bothered to compute a
  // person snapshot — on the fresh-sign-in path setCurrentPerson hasn't
  // run yet, and the plain trackEvent would drop the row.
  const emit = personOverride
    ? trackEventWithPerson('app.device_registered', data, personOverride)
    : trackEvent('app.device_registered', data);
  emit.catch(err => {
    logger.warn('Failed to track app.device_registered event', err);
  });
}

// Soft-refresh threshold. Bearer tokens have a hard 30-day server-side
// expiry; we ask the server to bump expires_at when the remaining window
// drops below this many milliseconds. With a 30-day TTL and a 14-day
// refresh cadence the threshold is 16 days — equivalent to "more than 14
// days into the window" but expressed against expires_at so the check
// self-stabilizes after a refresh.
//
// We intentionally do NOT compute staleness against registered_at: the
// server's refresh action bumps only expires_at (see
// Api::Offline::DevicesController#refresh in the companion PR), leaving
// registered_at unchanged. A registered_at-based check would re-fire on
// every validator tick after day 14, since "now - registered_at" only
// grows.
const SERVER_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const REFRESH_INTERVAL_MS = 14 * 24 * 60 * 60 * 1000;
const REFRESH_WHEN_REMAINING_LESS_THAN_MS = SERVER_TTL_MS - REFRESH_INTERVAL_MS;

let refreshInFlight: Promise<void> | null = null;

/**
 * If the locally-stored device token is more than 14 days old AND we're
 * online, ask the server to extend its 30-day window. The same bearer
 * keeps working; only `expires_at` is bumped. Failures are silent so the
 * caller (typically useDeviceValidator) can fire-and-forget on every
 * online tick.
 *
 * Forward-compat: if the server doesn't yet support `/refresh` (404/405/501)
 * we silently no-op. The existing bearer will keep working until its hard
 * expiry, at which point the user goes through `/login` → `/auth/callback`
 * → `POST /devices` as usual.
 */
export async function refreshDeviceIfStale(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    // getDeviceToken/getDeviceMetadata propagate DB errors (a transient
    // close during lock/unlock would otherwise silently no-op every
    // refresh tick until the user happens to be unlocked at exactly the
    // right moment). Wrap explicitly so a DB failure logs and defers
    // until the next validator tick, while a genuine "no token stored"
    // condition (row absent) still returns null and exits cleanly.
    let token: string | null;
    let metadata: DeviceMetadata | null;
    try {
      token = await getDeviceToken();
      metadata = await getDeviceMetadata();
    } catch (err) {
      logger.warn('Device read failed — deferring refresh (DB likely transitioning)', err);
      return;
    }
    if (!token || !metadata) return;

    const expiresAt = Date.parse(metadata.expires_at);
    if (Number.isNaN(expiresAt)) {
      logger.warn('Device metadata has unparseable expires_at — skipping refresh', metadata);
      return;
    }
    if (expiresAt - Date.now() > REFRESH_WHEN_REMAINING_LESS_THAN_MS) return;

    let response: Response;
    try {
      response = await fetchWithTimeout(API_ENDPOINTS.devicesRefresh, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Bearer is the only credential the refresh endpoint honors; sending
        // the cookie is unnecessary and leaves room for future server-side
        // logic to prefer it.
        credentials: 'omit',
        timeout: 10000,
      });
    } catch (error) {
      if (error instanceof NetworkError || error instanceof TimeoutError) {
        logger.info('Device refresh deferred — network error', error);
        return;
      }
      logger.warn('Device refresh deferred — unexpected error', error);
      return;
    }

    // Server doesn't speak /refresh yet — silently fall back; the existing
    // bearer will continue to work until its hard expiry.
    if (response.status === 404 || response.status === 405 || response.status === 501) {
      logger.info('Device refresh endpoint not available — keeping current bearer');
      return;
    }

    if (response.status === 401) {
      // The bearer the refresh was supposed to extend is no longer valid
      // (revoked, expired, or unknown). Don't throw here — the next regular
      // API call will hit the same 401 and fetchWithAuth's standard cleanup
      // path will handle it cleanly. Throwing would create a duplicate
      // softSignOut from whichever caller awaited refreshDeviceIfStale.
      logger.info('Device refresh rejected — deferring to standard 401 handling on next call');
      return;
    }

    if (!response.ok) {
      // Includes 403 (auth ok, permission denied) — surfaces as a noisy
      // failure but the existing bearer keeps working until its hard expiry.
      logger.warn(`Device refresh failed with ${response.status} — keeping current bearer`);
      return;
    }

    let payload: { device?: DeviceMetadata } | null = null;
    try {
      payload = await response.json() as { device?: DeviceMetadata };
    } catch (parseError) {
      // Malformed JSON in a 2xx response shouldn't escape the IIFE — the
      // refresh is best-effort; keep the existing bearer and try again
      // on the next online tick.
      logger.warn('Device refresh response was not valid JSON — keeping current bearer', parseError);
      return;
    }
    if (!payload?.device?.id || !payload?.device?.expires_at) {
      logger.warn('Device refresh response missing device metadata — keeping current bearer');
      return;
    }

    await setDeviceMetadata(payload.device);
    logger.info('Device refreshed', { id: payload.device.id, expires_at: payload.device.expires_at });
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Mark session as established by fetching and storing the current user data.
 * Called from the /auth/callback handler — the Rails session cookie was set
 * when the user signed in on the online Mon Ami app, then the browser was
 * redirected back to the offline app's callback to bootstrap local state.
 */
export async function setSessionEstablished(): Promise<void> {
  try {
    // Register the device BEFORE the fetchCurrentUser call so the rest of the
    // bootstrap (and every subsequent API call) runs on bearer auth instead of
    // the cookie. registerDeviceIfNeeded() is forward-compatible — if the
    // server hasn't shipped device support yet it silently no-ops.
    const registeredDevice = await registerDeviceIfNeeded();

    const currentUser = await fetchCurrentUser();
    await setItem(CURRENT_USER_ID_KEY, currentUser.id);
    // Store full user data for access to people array
    await setCurrentUser(currentUser);

    // Attribute events to this user right away; re-synced below once the
    // person is known so replays and issues show who was working.
    await syncSentryUser();

    // Auto-select the only available person so the caller can navigate
    // straight to the app without bouncing through /select-organization.
    // Overwriting directly (rather than clearing any stale person first and
    // then setting) avoids a transient null window where useLiveQuery
    // observers can see "no person" between the two writes — on slower
    // browsers that race triggers AuthWrapper's redirect to org selection.
    if (currentUser.people?.length === 1) {
      const person = currentUser.people[0];
      await setCurrentPerson(person);
      await syncSentryUser();
      // Emit device_registered AFTER setCurrentPerson — trackEvent drops the
      // event when there's no current_person, and on first sign-in this is
      // the only moment the freshly-registered device row would otherwise
      // surface in analytics.
      if (registeredDevice) {
        trackDeviceRegisteredEvent(registeredDevice, {
          personId: person.id,
          organizationId: person.organization.id,
        });
      }
      Promise.all([refreshPrograms(), refreshSites()]).catch((err) => {
        logger.warn('Failed to refresh programs/sites after auto-selecting person', err);
      });
      return;
    }

    // Multi-person (or no-person) case: validate the stored person against
    // the current user's people list. If it no longer belongs (e.g., the
    // user switched accounts in the online app), clear it so the user is
    // routed through /select-organization.
    const storedPerson = await getCurrentPerson();
    if (storedPerson) {
      const personStillValid = currentUser.people?.some(
        (p) => p.id === storedPerson.id,
      );
      if (!personStillValid) {
        logger.info('Stored person no longer valid for current user — clearing for org re-selection', {
          storedPersonId: storedPerson.id,
          availablePeople: currentUser.people?.map((p) => p.id),
        });
        await clearCurrentPerson();
      }
    }

    // Multi-person path: attribute the device_registered event to whichever
    // person we can still tie to. Prefer the stored person if it's still
    // valid (re-auth after expiry where the user keeps their org), otherwise
    // fall back to the first person in the user's list so the event isn't
    // dropped on the rare brand-new-multi-org sign-in. Either way the event
    // is anchored to a real person row the data team can join against.
    if (registeredDevice) {
      const validStoredPerson = storedPerson && currentUser.people?.some(p => p.id === storedPerson.id) ? storedPerson : null;
      const attributablePerson = validStoredPerson ?? currentUser.people?.[0];
      if (attributablePerson) {
        trackDeviceRegisteredEvent(registeredDevice, {
          personId: attributablePerson.id,
          organizationId: attributablePerson.organization.id,
        });
      }
    }
  } catch (error) {
    // Preserve typed auth errors so AuthCallback (and any future
    // caller branching on instanceof) gets the same signal the inner
    // helpers documented they would propagate. Wrapping in a generic
    // Error here silently demoted the bearer-registration 401 path
    // away from the standard session-expired UX.
    if (error instanceof AuthExpiredError || error instanceof UserNotFoundError) {
      throw error;
    }
    logger.error('Failed to fetch current user', error);
    throw new Error('Failed to establish session. Please try logging in again.');
  }
}

/**
 * Best-effort: tell the server to revoke the current device's bearer token
 * before the local DB wipe in logout(). All failures (no token stored,
 * offline, server doesn't speak this endpoint yet, 401, network blip) are
 * swallowed — the user's intent is already "log me out locally," and
 * leaving an orphan device row server-side is recoverable from the
 * org-admin UI or the 30-day TTL.
 *
 * NOTE — locked-screen logout: the "Logout and Wipe Device" button on
 * LockScreen calls logout() while the DB is still closed, so
 * getDeviceToken() throws and we silently skip the server-side revoke.
 * The result is an orphan device row in the org-admin Offline Devices
 * list until its 30-day TTL or an explicit admin revoke. This is by
 * design: the bearer is stored in the dexie-encrypted applicationSession
 * (encryption key derived from the user's WebAuthn passkey), so a locked
 * client genuinely cannot read its own bearer to authenticate the
 * revoke. Do NOT "fix" this by moving the bearer outside the encrypted
 * middleware — at-rest encryption of the bearer is what makes a stolen
 * device useless without the passkey, and that property is more valuable
 * than a clean device list on the rare locked-logout path.
 */
async function revokeCurrentDeviceOnServer(): Promise<void> {
  let token: string | null = null;
  try {
    token = await getDeviceToken();
  } catch (err) {
    // Most commonly: DatabaseClosedError because logout() was triggered
    // from the lock screen. See the locked-screen logout note in the
    // function docstring above for why this is intentional.
    logger.warn('Could not read device token for revoke — skipping', err);
    return;
  }
  if (!token) return;

  // Fire-and-forget. Awaiting this would block the user-visible "Logout
  // and Wipe Device" confirmation behind a network round-trip — bad UX
  // when the server is slow or unreachable. `keepalive: true` lets the
  // request survive the page unload the user typically triggers right
  // after logout. Bearer is the only credential the server is contracted
  // to honor here; `credentials: 'omit'` keeps the cookie out so a
  // future server-side "prefer cookie" change can't silently break
  // logout. Errors are swallowed: the user's intent is already "log me
  // out locally," and leaving an orphan device row server-side is
  // recoverable from the org-admin UI or the 30-day TTL.
  void fetch(API_ENDPOINTS.devicesCurrent, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
    keepalive: true,
  }).catch((err) => {
    logger.info('Device revoke call failed — local logout already in progress', err);
  });
}

/**
 * Log out the user by clearing ALL local data
 * This includes session data, credentials, and the entire database
 * This is a local operation for the offline-first PWA
 * The session cookie will naturally expire or be invalidated on next API request
 */
export async function logout(): Promise<void> {
  logger.debug('Logging out - clearing all local data...');

  clearSentryUser();

  // Best-effort server-side device revoke BEFORE we wipe the local DB —
  // otherwise we lose the bearer token needed to identify which device to
  // revoke. Failures here (offline, server hasn't shipped the endpoint yet,
  // already-revoked, etc.) don't block local cleanup; the device will
  // either expire on its 30-day TTL or be revoked from the org-admin UI.
  await revokeCurrentDeviceOnServer();

  // Delete the entire database. This wipes session data, credentials, and
  // user data in a single step, and works even if the DB has already been
  // closed (e.g., when locked). Per-key removeItem calls before this are
  // redundant and would log spurious "DatabaseClosedError" entries when the
  // DB is already closed — db.delete() doesn't require an open handle.
  try {
    if (db.isOpen()) {
      db.close();
    }
    await db.delete();
    logger.debug('Database deleted successfully');
  } catch (error) {
    logger.error('Failed to delete database during logout:', error);
    // Continue with logout even if database deletion fails
  }

  // 4. Clear runtime caches but preserve the precache, fonts and images.
  // The precache holds the whole app shell and every script and stylesheet
  // (build artifacts only — no sensitive data); clearing it would break
  // offline start until the SW re-precaches everything.
  const PRESERVED_CACHE_PREFIXES = ['serwist-precache', 'fonts-cache', 'images-cache'];
  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const cacheNames = await caches.keys();
      const cachesToDelete = cacheNames.filter(
        name => !PRESERVED_CACHE_PREFIXES.some(prefix => name.includes(prefix))
      );
      await Promise.all(cachesToDelete.map(cacheName => caches.delete(cacheName)));
      logger.debug(`Cleared ${cachesToDelete.length} runtime cache(s), preserved ${cacheNames.length - cachesToDelete.length} static cache(s)`);
    } catch (error) {
      logger.error('Failed to clear service worker caches:', error);
      // Continue with logout even if cache clearing fails
    }
  }

  // 5. Redirect to login page (local operation)
  redirectToLogin();

  logger.debug('Logout complete');
}

/**
 * Check if user has completed the full authentication flow
 * (has a valid session AND has selected a person)
 * With session-based auth, we check if a person is selected
 */
export async function isFullyAuthenticated(): Promise<boolean> {
  const person = await getCurrentPerson();
  return person !== null;
}

/**
 * Redirect to login if not fully authenticated (session + person selected)
 * @param router - Optional Next.js router for client-side navigation
 */
export async function requireFullAuth(router?: { push: (path: string) => void }): Promise<boolean> {
  const authenticated = await isFullyAuthenticated();
  if (!authenticated) {
    if (router) {
      router.push('/login');
    } else if (typeof window !== 'undefined') {
      if (navigator.onLine) {
        window.location.href = '/login';
      } else {
        window.location.href = '/offline';
      }
    }
    return false;
  }
  return true;
}

/**
 * Redirect to the login page.
 * Used by both logout() and lighter auth-expiry handlers so the redirect
 * target is defined in one place.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  // Already on /login — assigning location.href triggers a full reload,
  // which remounts the app and re-runs any session-validation effects
  // that brought us here, producing a flashing reload loop.
  if (window.location.pathname === '/login') return;
  window.location.href = '/login';
}

/**
 * Soft sign-out — clear local auth identifiers and the device token, then
 * route to /login. Does NOT wipe the encrypted DB; the user's clients,
 * documents, and unsynced field work survive so they can re-authenticate
 * as the same user and continue exactly where they left off.
 *
 * Use this for any IMPLICIT auth-expiry path: bearer-token revoked or
 * expired, 401 on a routine fetch, session validator drift, unlock-time
 * session check failure. The encrypted DB is the most valuable thing on
 * the device — burning it is reserved for `logout()`, which is the
 * explicit "Logout and wipe device" user action plus the rule-2
 * server-confirmed unrecoverable state (UserNotFoundError) per the
 * session-handling contract documented at the top of this file.
 */
export async function softSignOut(): Promise<void> {
  logger.info('softSignOut — clearing identifiers + device token, preserving local DB');

  // The clears touch independent rows in applicationSession and can run
  // in parallel. softSignOut is on the unlock-time UI path; squeezing
  // out a few hundred ms matters there. allSettled (not all) so a single
  // failure — e.g., the DB is closed when called from a locked context —
  // doesn't drop the other clears or block the redirect.
  //
  // current_user (the cached CurrentUserResponse) clears alongside
  // current_user_id so useSessionValidator's mount-time Sentry
  // rehydration can't pick up a stale identity after a soft sign-out
  // path that didn't go through fetchCurrentUser (which clears it on
  // 401 independently).
  //
  // Each clear is paired with its label in one object so reordering
  // can't desync the warning logs.
  const clears = [
    { label: 'clearDevice', run: clearDevice() },
    { label: 'clearCurrentPerson', run: clearCurrentPerson() },
    { label: 'clearCurrentUser', run: clearCurrentUser() },
    { label: 'removeItem(current_user_id)', run: removeItem(CURRENT_USER_ID_KEY) },
  ];
  const results = await Promise.allSettled(clears.map((c) => c.run));
  results.forEach((result, idx) => {
    if (result.status === 'rejected') {
      logger.warn(`softSignOut: ${clears[idx].label} failed`, result.reason);
    }
  });

  clearSentryUser();
  redirectToLogin();
}

/**
 * Check if the session is still valid by making a lightweight API request
 * This is used during unlock to ensure the session hasn't expired while locked
 * @returns Promise<boolean> - true if session is valid, false otherwise
 */
export async function checkSessionValidity(): Promise<boolean> {
  // If offline, assume session is valid (can't check)
  if (typeof window !== 'undefined' && !navigator.onLine) {
    logger.info('Browser is offline — assuming session is valid');
    return true;
  }

  try {
    // Prefer bearer auth when a device token is stored. The unlock path is
    // the load-bearing case — `LockContext.unlockScreen` routes the user
    // to softSignOut when this returns false. Asking the cookie alone
    // here would mean a cookie-inactivity expiry takes out the user's
    // unsynced field work even though the bearer is still valid, which
    // is the exact failure mode this PR exists to fix.
    //
    // `.catch(() => null)` so a transient Dexie throw during the unlock
    // window (DB momentarily closed between lock and unlock) doesn't
    // propagate to LockContext, which expects a boolean. Falls through
    // to the cookie path, which the connectivity check tolerates.
    const token = await getDeviceToken().catch(() => null);
    const headers: Record<string, string> = { Accept: 'application/json' };
    let credentials: 'omit' | 'include' = 'include';
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      credentials = 'omit';
    }

    const response = await fetchWithTimeout(API_ENDPOINTS.currentUser, {
      method: 'HEAD',
      headers,
      credentials,
      timeout: 5000, // 5 second timeout
    });

    // Session is valid if we get 2xx response
    // 401/403 means session expired, other errors are network issues
    return response.ok;
  } catch (error) {
    // Any network-related error means we can't reach the server — allow unlock.
    // This includes: NetworkError (from fetchWithTimeout), TypeError (native fetch),
    // and TimeoutError (from fetchWithTimeout abort).
    if (error instanceof NetworkError || error instanceof TypeError || error instanceof TimeoutError) {
      logger.warn('Network error during session check — allowing unlock', error instanceof Error ? error.message : error);
      return true;
    }

    // Other errors - deny unlock for safety
    logger.warn('Unknown error during session check — denying unlock', error);
    return false;
  }
}
